import { debounce } from "lodash"
import { isValid } from "date-fns"
import {
  type Filter,
  type FilterFieldType,
  disabledFilterFields,
  getFilterOperators,
} from "~/utils/filter"
import { Input, NumericInput } from "~/components/reusable/Input"
import Select, { UserSelect } from "~/components/reusable/Select"
import DatePicker from "~/components/reusable/DatePicker"
import CollectionsSelect from "~/components/collections/CollectionsSelect"
import { FilterAsyncEnumSelect } from "./FilterAsyncEnumSelect"
import { FilterExternalEnumSelect } from "./FilterExternalEnumSelect"
import { useFilterFields } from "~/hooks/useFilterFields"
import { useGetUsers } from "~/hooks/useUsers"
import { isDevelopment } from "~/utils/environment"
import { filterNotUndefinedOrNull } from "~/utils/tsUtils"
import { useMemo } from "react"

interface FilterValueInputProps {
  filter: Filter
  onValueChange: (value: string) => void
  onValuesChange: (values: string[]) => void
  size?: "sm" | "base"
  autoFocus?: boolean
  /** Context-specific fields appended to the defaults (e.g. the supply plan's SKU Size). */
  extraFilterFields?: FilterFieldType[]
}

export function FilterValueInput({
  filter,
  onValueChange,
  onValuesChange,
  size = "base",
  autoFocus = false,
  extraFilterFields,
}: FilterValueInputProps) {
  const {
    allFilterFields,
    enumLookupTable,
    forecastModelsOptions,
    computeOptionsForField,
    computeGroupedOptionsForField,
    tags,
    collections,
  } = useFilterFields("", extraFilterFields)
  const { data: users } = useGetUsers(isDevelopment())

  // Find field and operator from the filter
  const field = allFilterFields.find(
    (f: FilterFieldType) =>
      f.value === filter.field ||
      (f.value === "DynamicFilterField" && f.dynamicValue === filter.field),
  )
  const operator = getFilterOperators(filter.field, allFilterFields).find(
    (op) => op.value === filter.operator,
  )

  // Derive these defensively so the hooks below can run unconditionally.
  const fieldValue =
    field?.value === "DynamicFilterField" ? (field.dynamicValue ?? "") : (field?.value ?? "")
  const fieldType = field?.type
  const groupedOptions = useMemo(
    () => computeGroupedOptionsForField(fieldValue, fieldType),
    [computeGroupedOptionsForField, fieldValue, fieldType],
  )
  const relevantValuesSet = useMemo(
    () => (groupedOptions.relevant.length > 0 ? new Set(groupedOptions.relevant) : undefined),
    [groupedOptions],
  )

  if (!field || !operator) return null

  // Valueless operators (e.g. isEmpty / isNotEmpty) have no companion input.
  if (operator.valueless) return null

  const currentValue = filter.value ?? ""
  const currentValues = filter.values ?? []

  // Compute options for field if needed
  const fieldOptions = computeOptionsForField(fieldValue, field.type)

  // Debounced callbacks
  const debouncedOnValueChange = debounce((value: string) => {
    if (value !== "") {
      onValueChange(value)
    }
  }, 1000)

  // Handle array value operators
  if (operator.arrayValue) {
    if (field.type === "collection") {
      return (
        <div className="min-w-[200px] max-w-[350px]">
          <CollectionsSelect
            selectedCollectionIds={
              filterNotUndefinedOrNull(
                currentValues.map((value) => collections?.find((x) => x.value == value)?.id),
              ) ?? []
            }
            onCollectionSelectChange={(option) => onValuesChange(option?.map((c) => c.name) ?? [])}
            isFilter
            isMulti
            autoFocus={autoFocus}
          />
        </div>
      )
    } else if (
      fieldValue?.includes("(extradata)") ||
      fieldValue?.includes("(collection-extradata)")
    ) {
      return (
        <div className="min-w-[200px] max-w-[350px]">
          <FilterExternalEnumSelect
            columnName={fieldValue}
            columnValue=""
            onValueChange={(value) => onValueChange(value ?? "")}
            isMulti={true}
            selectedValues={currentValues}
            onValuesChange={(values) => onValuesChange(values ?? [])}
            autoFocus={autoFocus}
            relevantValues={relevantValuesSet}
          />
        </div>
      )
    } else {
      return (
        <div className="min-w-[200px] max-w-[350px]">
          <FilterAsyncEnumSelect
            isMulti
            options={fieldOptions}
            groupedOptions={groupedOptions}
            values={currentValues}
            onValuesChange={onValuesChange}
            isDisabled={!fieldOptions.length}
            placeholder={fieldOptions.length ? "Select values" : "No values"}
            size={size}
            autoFocus={autoFocus}
          />
        </div>
      )
    }
  }

  // Handle string operators that need text input
  if (["startsWith", "endsWith", "contains", "doesNotContain"].includes(operator.value)) {
    return (
      <Input
        key={`${fieldValue}-${currentValue}`}
        placeholder="Type value"
        defaultValue={currentValue}
        onChange={(e) => {
          debouncedOnValueChange(e.target.value)
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault()
            const target = e.target as HTMLInputElement
            debouncedOnValueChange.cancel()
            onValueChange(target.value)
          }
        }}
        containerClassName="w-full"
        autoFocus={autoFocus}
      />
    )
  }

  // Handle different field types
  if (fieldValue === "tags") {
    return (
      <Select
        options={tags}
        getOptionValue={(tag) => tag.name}
        getOptionLabel={(tag) => tag.name}
        onChange={(tag) => onValueChange(tag?.name ?? "")}
        value={tags?.find((tag) => tag.name === currentValue)}
        openMenuOnFocus
        size={size}
        autoFocus={autoFocus}
      />
    )
  } else if (field.type === "user") {
    return (
      <UserSelect
        onChange={(user) => onValueChange(user?.value ?? "")}
        selectedUser={users?.find((user) => user.email === currentValue)}
        users={users ?? []}
        isClearable
        placeholder="Select user"
        openMenuOnFocus
        autoFocus={autoFocus}
      />
    )
  } else if (enumLookupTable[fieldValue as keyof typeof enumLookupTable]) {
    const enumOptions = filterNotUndefinedOrNull(
      enumLookupTable[fieldValue as keyof typeof enumLookupTable] ?? [],
    )
      .map((e: any) => e.value as string | undefined)
      .filter((v): v is string => !!v)
    return (
      <FilterAsyncEnumSelect
        options={enumOptions}
        groupedOptions={groupedOptions}
        value={currentValue}
        onValueChange={onValueChange}
        size={size}
        autoFocus={autoFocus}
      />
    )
  } else if (field.type === "date") {
    return (
      <DatePicker
        fullWidth
        value={currentValue && isValid(new Date(currentValue)) ? new Date(currentValue) : undefined}
        onChange={(date) => date && onValueChange(date.toISOString())}
      />
    )
  } else if (fieldValue?.includes("(extradata)") && field.type !== "number") {
    return (
      <FilterExternalEnumSelect
        columnName={fieldValue}
        columnValue={currentValue}
        onValueChange={(value) => onValueChange(value)}
        relevantValues={relevantValuesSet}
      />
    )
  } else if (fieldValue === "model") {
    return (
      <Select
        options={forecastModelsOptions}
        getOptionValue={(option) => option.value}
        getOptionLabel={(option) => option.value}
        onChange={(option) => onValueChange(option?.value ?? "")}
        value={forecastModelsOptions?.find((option: any) => option.value === currentValue)}
        openMenuOnFocus
        size={size}
        autoFocus={autoFocus}
      />
    )
  } else if (field.type === "number") {
    return (
      <NumericInput
        key={`${fieldValue}-${currentValue}`}
        placeholder="Type value"
        value={currentValue}
        onChange={(num) => onValueChange(String(num))}
        allowDecimals
        allowNegative
        allowZero
        containerClassName="w-full"
        autoFocus={autoFocus}
        unit={field.unit}
      />
    )
  } else if (field.type === "boolean") {
    return (
      <Select
        options={[
          { value: "true", label: "True" },
          { value: "false", label: "False" },
        ]}
        onChange={(option) => onValueChange(option?.value ?? "")}
        value={
          currentValue === "true"
            ? { value: "true", label: "True" }
            : currentValue === "false"
              ? { value: "false", label: "False" }
              : null
        }
        placeholder="Select value"
        openMenuOnFocus
        size={size}
        autoFocus={autoFocus}
      />
    )
  } else {
    // Default input field
    return (
      <Input
        key={`${fieldValue}-${currentValue}`}
        placeholder="Type value"
        defaultValue={currentValue}
        onChange={(e) => {
          debouncedOnValueChange(e.target.value)
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault()
            const target = e.target as HTMLInputElement
            debouncedOnValueChange.cancel()
            onValueChange(target.value)
          }
        }}
        disabled={disabledFilterFields.includes(fieldValue)}
        containerClassName="w-full"
        autoFocus={autoFocus}
      />
    )
  }
}
